import React, { useState, useEffect, useCallback } from 'react';
import { KpiCard, toneColor } from '@/design-system/dna';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AlertTriangle, CheckCircle2, Clock, Activity, Zap, Sparkles,
  ArrowRight, ChevronRight, RefreshCw, Filter, BarChart3,
  Wallet, GraduationCap, MessageSquare, Scale, Shield,
  AlertCircle, Users, Radio, Target, FileText, ExternalLink
} from 'lucide-react';
import { cn } from '../../../lib/utils';

const API_URL = window.location.origin || '';
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.05 } } };
const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] } } };

// ── Type config ─────────────────────────────
const typeConfig = {
  financeiro: { icon: Wallet, color: '#059669', bg: '#ECFDF5', border: '#D1FAE5', label: 'Financeiro' },
  academico: { icon: GraduationCap, color: '#7B3F98', bg: '#F5F3FF', border: '#EDE9FE', label: 'Acadêmico' },
  fiscal: { icon: Scale, color: '#0891B2', bg: '#ECFEFF', border: '#CFFAFE', label: 'Fiscal' },
  comunicacao: { icon: MessageSquare, color: '#2563EB', bg: '#EFF6FF', border: '#DBEAFE', label: 'Comunicação' },
  operacional: { icon: Shield, color: '#D97706', bg: '#FFFBEB', border: '#FEF3C7', label: 'Operacional' },
};
const prioConfig = {
  alta: { color: '#E11D48', bg: '#FFF1F2', label: 'Alta', border: '#FFE4E6' },
  media: { color: '#D97706', bg: '#FFFBEB', label: 'Média', border: '#FEF3C7' },
  baixa: { color: '#2563EB', bg: '#EFF6FF', label: 'Baixa', border: '#DBEAFE' },
};

// ── Mission Status ──────────────────────────
const OperationMission = ({ data, onRefresh }) => {
  const total = data.total || 0;
  const criticas = (data.pendencias || []).filter(p => p.prioridade === 'alta').length;
  const status = criticas > 5 ? 'critico' : total > 10 ? 'atencao' : total > 0 ? 'operacional' : 'limpo';
  const cfg = {
    limpo: { l: 'Sem Pendências', c: '#10B981' },
    operacional: { l: 'Operacional', c: '#F59E0B' },
    atencao: { l: 'Atenção Requerida', c: '#F59E0B' },
    critico: { l: 'Estado Crítico', c: '#EF4444' },
  }[status];

  return (
    <motion.div variants={fadeUp} data-testid="operation-mission"
      className="rounded-2xl px-5 py-3.5 flex items-center justify-between border"
      style={{ background: `${cfg.c}06`, borderColor: `${cfg.c}15` }}>
      <div className="flex items-center gap-4">
        <motion.div className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: cfg.c, boxShadow: `0 0 8px ${cfg.c}60` }}
          animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 2, repeat: Infinity }} />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">DNA Operation Control</span>
        <div className="h-3.5 w-px bg-slate-200" />
        <span className="text-sm font-semibold" style={{ color: cfg.c }}>{cfg.l}</span>
        <div className="h-3.5 w-px bg-slate-200" />
        <span className="text-[11px] text-slate-400"><Clock className="h-3 w-3 inline mr-1" />{new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
      </div>
      <button onClick={onRefresh} className="flex items-center gap-1.5 text-slate-400 hover:text-slate-600 transition-colors">
        <RefreshCw className="h-3.5 w-3.5" /><span className="text-[11px]">Atualizar</span>
      </button>
    </motion.div>
  );
};

// ── KPI Card ────────────────────────────────
const KPICard = ({ title, value, icon, color, sub }) => (
  <KpiCard id={`op-${String(title).toLowerCase().replace(/\s+/g, '-')}`} icon={icon} label={title} value={value} hint={sub} color={toneColor(color)} />
);

// ── Pendência Card ──────────────────────────
const PendenciaCard = ({ item, onAction }) => {
  const tc = typeConfig[item.tipo] || typeConfig.operacional;
  const pc = prioConfig[item.prioridade] || prioConfig.media;
  const Icon = tc.icon;

  return (
    <motion.div variants={fadeUp} data-testid={`pendencia-${item.tipo}`}
      className="rounded-2xl p-5 bg-white border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-[1px] transition-all group">
      <div className="flex items-start gap-4">
        {/* Type icon */}
        <div className="p-2.5 rounded-xl flex-shrink-0" style={{ background: tc.bg, border: `1px solid ${tc.border}` }}>
          <Icon className="h-5 w-5" style={{ color: tc.color }} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <h3 className="text-[14px] font-semibold text-slate-800">{item.titulo}</h3>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border"
              style={{ background: pc.bg, borderColor: pc.border, color: pc.color }}>
              {pc.label}
            </span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium border"
              style={{ background: tc.bg, borderColor: tc.border, color: tc.color }}>
              {tc.label}
            </span>
          </div>
          <p className="text-[12px] text-slate-500 leading-relaxed">{item.descricao}</p>
          <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-400">
            <span className="flex items-center gap-1"><Target className="h-3 w-3" />{item.quantidade} {item.quantidade === 1 ? 'item' : 'itens'}</span>
            <span className="flex items-center gap-1"><Activity className="h-3 w-3" />{item.modulo}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          {item.acao && (
            <button onClick={() => onAction(item.acao)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-medium text-white transition-all hover:shadow-lg"
              style={{ backgroundColor: tc.color }}>
              <ExternalLink className="h-3 w-3" /> Resolver
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════
export default function CentralOperacional() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterPrio, setFilterPrio] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const h = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/api/v2/central`, { headers: h });
      setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="space-y-5 animate-pulse" data-testid="central-loading">
        <div className="h-12 rounded-2xl bg-slate-100" />
        <div className="grid grid-cols-6 gap-3">{[...Array(6)].map((_, i) => <div key={i} className="h-24 rounded-2xl bg-slate-100" />)}</div>
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-28 rounded-2xl bg-slate-100" />)}</div>
      </div>
    );
  }

  const d = data || {};
  const pendencias = d.pendencias || [];
  const resumo = d.resumo || {};
  const total = d.total || 0;
  const criticas = pendencias.filter(p => p.prioridade === 'alta').length;
  const medias = pendencias.filter(p => p.prioridade === 'media').length;

  // Filter
  const filtered = pendencias.filter(p => {
    if (filterType !== 'all' && p.tipo !== filterType) return false;
    if (filterPrio !== 'all' && p.prioridade !== filterPrio) return false;
    return true;
  });

  // Insights
  const insights = [];
  if (resumo.financeiras > 0) insights.push({ text: `${resumo.financeiras} pendência(s) financeira(s) detectada(s).`, action: 'Abrir Financeiro', color: '#059669', path: '/v2/financeiro' });
  if (resumo.academicas > 0) insights.push({ text: `${resumo.academicas} pendência(s) acadêmica(s) — professores sem notas.`, action: 'Abrir Acadêmico', color: '#7B3F98', path: '/v2/academico/notas' });
  if (resumo.fiscais > 0) insights.push({ text: `${resumo.fiscais} nota(s) fiscal(is) pendente(s) de emissão.`, action: 'Abrir Fiscal', color: '#0891B2', path: '/v2/financeiro/fiscal' });
  if (resumo.operacionais > 0) insights.push({ text: `${resumo.operacionais} item(ns) operacional(is) no DNA GO.`, action: 'Abrir Operação', color: '#D97706', path: '/v2/operacao' });
  if (insights.length === 0) insights.push({ text: 'Nenhuma pendência detectada. Sistema operando normalmente.', color: '#10B981' });

  const filterBtnClass = (active) => cn(
    "px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all border",
    active ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
  );

  return (
    <motion.div className="space-y-5" initial="hidden" animate="visible" variants={stagger} data-testid="central-operacional">

      {/* 1. MISSION STATUS */}
      <OperationMission data={d} onRefresh={load} />

      {/* 2. KPIs */}
      <motion.div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" variants={stagger}>
        <KPICard title="Total Pendências" value={total} icon={Activity} color={total > 10 ? 'rose' : 'purple'} />
        <KPICard title="Críticas" value={criticas} icon={AlertTriangle} color={criticas > 0 ? 'rose' : 'green'} sub={criticas > 0 ? 'Ação imediata' : 'Nenhuma'} />
        <KPICard title="Atenção" value={medias} icon={AlertCircle} color={medias > 0 ? 'amber' : 'green'} />
        <KPICard title="Acadêmicas" value={resumo.academicas || 0} icon={GraduationCap} color="purple" />
        <KPICard title="Financeiras" value={resumo.financeiras || 0} icon={Wallet} color="green" />
        <KPICard title="Fiscais" value={resumo.fiscais || 0} icon={Scale} color="cyan" />
      </motion.div>

      {/* 3. FILTERS + QUEUE */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Queue — 2 cols */}
        <div className="lg:col-span-2 space-y-4">
          {/* Filter bar */}
          <motion.div variants={fadeUp} className="flex items-center gap-2 flex-wrap">
            <Filter className="h-4 w-4 text-slate-400" />
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 mr-2">Tipo:</span>
            <button className={filterBtnClass(filterType === 'all')} onClick={() => setFilterType('all')}>Todos</button>
            <button className={filterBtnClass(filterType === 'financeiro')} onClick={() => setFilterType('financeiro')}>Financeiro</button>
            <button className={filterBtnClass(filterType === 'academico')} onClick={() => setFilterType('academico')}>Acadêmico</button>
            <button className={filterBtnClass(filterType === 'fiscal')} onClick={() => setFilterType('fiscal')}>Fiscal</button>
            <button className={filterBtnClass(filterType === 'comunicacao')} onClick={() => setFilterType('comunicacao')}>Comunicação</button>
            <button className={filterBtnClass(filterType === 'operacional')} onClick={() => setFilterType('operacional')}>Operacional</button>

            <div className="h-4 w-px bg-slate-200 mx-1" />
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 mr-2">Prioridade:</span>
            <button className={filterBtnClass(filterPrio === 'all')} onClick={() => setFilterPrio('all')}>Todas</button>
            <button className={filterBtnClass(filterPrio === 'alta')} onClick={() => setFilterPrio('alta')}>Alta</button>
            <button className={filterBtnClass(filterPrio === 'media')} onClick={() => setFilterPrio('media')}>Média</button>
          </motion.div>

          {/* Queue header */}
          <motion.div variants={fadeUp} className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-rose-500" />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Fila de Execução</span>
            <span className="text-[10px] text-slate-300 ml-1">({filtered.length} {filtered.length === 1 ? 'item' : 'itens'})</span>
          </motion.div>

          {/* Queue items */}
          <motion.div className="space-y-3" variants={stagger}>
            {filtered.length === 0 ? (
              <motion.div variants={fadeUp} className="rounded-2xl p-12 bg-white border border-slate-100 text-center">
                <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
                <p className="text-[15px] font-semibold text-slate-700">Nenhuma pendência</p>
                <p className="text-[12px] text-slate-400 mt-1">
                  {filterType !== 'all' || filterPrio !== 'all' ? 'Nenhum item corresponde aos filtros selecionados.' : 'Todas as pendências foram resolvidas. Sistema operando normalmente.'}
                </p>
              </motion.div>
            ) : (
              filtered.map((item, i) => (
                <PendenciaCard key={i} item={item} onAction={(path) => navigate(path)} />
              ))
            )}
          </motion.div>
        </div>

        {/* Insights — 1 col */}
        <div className="space-y-4">
          {/* IA Insights */}
          <motion.div variants={fadeUp}>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-3.5 w-3.5 text-violet-500" />
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">IA Operacional</span>
            </div>
            <div className="rounded-2xl p-4 space-y-3 bg-white border border-slate-100 shadow-sm">
              {insights.map((ins, i) => (
                <button key={i} onClick={() => ins.path && navigate(ins.path)}
                  className="w-full p-3.5 rounded-xl text-left border-l-[3px] hover:translate-x-0.5 transition-all"
                  style={{ borderLeftColor: ins.color, background: `${ins.color}06` }}>
                  <p className="text-[12px] text-slate-600 leading-relaxed">{ins.text}</p>
                  {ins.action && (
                    <span className="text-[11px] font-semibold mt-1.5 flex items-center gap-1" style={{ color: ins.color }}>
                      {ins.action} <ArrowRight className="h-3 w-3" />
                    </span>
                  )}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Distribution */}
          <motion.div variants={fadeUp}>
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="h-3.5 w-3.5 text-slate-400" />
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Distribuição</span>
            </div>
            <div className="rounded-2xl p-4 bg-white border border-slate-100 shadow-sm space-y-3">
              {Object.entries(resumo).filter(([, v]) => v > 0).map(([key, val]) => {
                const tc = typeConfig[key === 'financeiras' ? 'financeiro' : key === 'academicas' ? 'academico' : key === 'fiscais' ? 'fiscal' : key === 'comunicacao' ? 'comunicacao' : 'operacional'];
                const pct = total > 0 ? Math.round(val / total * 100) : 0;
                return (
                  <div key={key}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[12px] text-slate-600 capitalize">{tc?.label || key}</span>
                      <span className="text-[12px] font-bold" style={{ color: tc?.color }}>{val} ({pct}%)</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <motion.div className="h-full rounded-full" style={{ backgroundColor: tc?.color, width: `${pct}%` }}
                        initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6, delay: 0.2 }} />
                    </div>
                  </div>
                );
              })}
              {total === 0 && <p className="text-[12px] text-slate-400 text-center py-2">Nenhuma pendência</p>}
            </div>
          </motion.div>

          {/* Quick Actions */}
          <motion.div variants={fadeUp}>
            <div className="flex items-center gap-2 mb-3">
              <Zap className="h-3.5 w-3.5 text-amber-500" />
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Ações Rápidas</span>
            </div>
            <div className="space-y-2">
              {[
                { label: 'Ver Acadêmico', icon: GraduationCap, color: '#7B3F98', path: '/v2/academico/notas' },
                { label: 'Ver Financeiro', icon: Wallet, color: '#059669', path: '/v2/financeiro' },
                { label: 'Ver Fiscal', icon: Scale, color: '#0891B2', path: '/v2/financeiro/fiscal' },
                { label: 'Ver DNA Go', icon: Shield, color: '#D97706', path: '/v2/operacao' },
              ].map((a, i) => (
                <button key={i} onClick={() => navigate(a.path)} data-testid={`central-action-${a.label.toLowerCase().replace(/\s/g, '-')}`}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-white border border-slate-100 hover:shadow-md hover:-translate-y-[1px] transition-all text-left">
                  <div className="p-1.5 rounded-lg" style={{ background: `${a.color}12` }}>
                    <a.icon className="h-3.5 w-3.5" style={{ color: a.color }} />
                  </div>
                  <span className="text-[12px] font-medium text-slate-600 flex-1">{a.label}</span>
                  <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
